import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Stat,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiPercent,
  FiBarChart2,
  FiEye,
  FiDownload,
  FiShare2,
  FiRefreshCw,
  FiMonitor,
  FiSmartphone,
  FiTablet,
  FiMessageCircle,
} from 'react-icons/fi';
import { 
  MdOutlineAttachMoney, 
  MdOutlineCampaign,
  MdOutlineBrush,
  MdOutlinePhotoCamera,
  MdOutlineWeb,
} from 'react-icons/md';

// Reusable components
const StatCard = ({ title, value, icon, change, colorScheme, formatter, isLoading, footerText }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

// Main Dashboard Component
const MarketingDashboard = () => {
  const [stats, setStats] = useState({
    newUsers: { value: 0, change: 0 },
    conversionRate: { value: 0, change: 0 },
    impressions: { value: 0, change: 0 },
    averageCPC: { value: 0, change: 0 },
    activePromos: { value: 0, change: 0 },
    engagementRate: { value: 0, change: 0 },
    appDownloads: { value: 0, change: 0 },
    roi: { value: 0, change: 0 },
  });
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [channelStats, setChannelStats] = useState([]);
  const [deviceStats, setDeviceStats] = useState([]);

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/marketing');
        // const data = await response.json();
        // setStats(data.stats);
        
        // Mock data for demo
        setTimeout(() => {
          setStats({
            newUsers: { value: 845, change: 18 },
            conversionRate: { value: 8.7, change: 3 },
            impressions: { value: 1250000, change: 22 },
            averageCPC: { value: 0.87, change: -12 },
            activePromos: { value: 6, change: 20 },
            engagementRate: { value: 4.3, change: 15 },
            appDownloads: { value: 2843, change: 35 },
            roi: { value: 327, change: 18 },
          });
          
          // Mock campaign data
          setCampaigns([
            { id: 1, name: 'Campanha Google Ads', status: 'Ativa', budget: 5000, spent: 3200, conversions: 156, cpa: 20.51, roi: 345 },
            { id: 2, name: 'Promoção Instagram', status: 'Ativa', budget: 3000, spent: 2800, conversions: 112, cpa: 25.00, roi: 280 },
            { id: 3, name: 'Facebook Ads', status: 'Ativa', budget: 4000, spent: 1800, conversions: 92, cpa: 19.57, roi: 368 },
            { id: 4, name: 'Campanha Email Marketing', status: 'Pausada', budget: 1000, spent: 800, conversions: 45, cpa: 17.78, roi: 410 },
            { id: 5, name: 'Campanha Relançamento App', status: 'Ativa', budget: 7500, spent: 4200, conversions: 230, cpa: 18.26, roi: 395 },
          ]);
          
          // Mock channel data
          setChannelStats([
            { channel: 'Pesquisa Orgânica', users: 4350, conversion: 2.8, roi: 0 },
            { channel: 'Pesquisa Paga', users: 3120, conversion: 3.5, roi: 320 },
            { channel: 'Social Media', users: 2860, conversion: 2.3, roi: 275 },
            { channel: 'Email Marketing', users: 1970, conversion: 4.2, roi: 410 },
            { channel: 'Direto', users: 1540, conversion: 5.1, roi: 0 },
          ]);
          
          // Mock device data
          setDeviceStats([
            { type: 'Desktop', percentage: 38, conversion: 4.2 },
            { type: 'Mobile', percentage: 52, conversion: 3.1 },
            { type: 'Tablet', percentage: 10, conversion: 3.8 },
          ]);
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard de Marketing</Heading>
          <Text color="gray.500">Análise de campanhas e métricas de aquisição</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Main Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Novos Usuários" 
          value={stats.newUsers.value} 
          icon={FiUsers} 
          change={stats.newUsers.change} 
          colorScheme="blue" 
          formatter={formatNumber}
          isLoading={isLoading} 
          footerText="Período atual"
        />
        <StatCard 
          title="Taxa de Conversão" 
          value={`${stats.conversionRate.value}%`} 
          icon={FiPercent} 
          change={stats.conversionRate.change} 
          colorScheme="green" 
          isLoading={isLoading}
          footerText="Visitantes → Clientes"
        />
        <StatCard 
          title="Impressões" 
          value={stats.impressions.value} 
          icon={FiEye} 
          change={stats.impressions.change} 
          colorScheme="purple" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Total visualizações"
        />
        <StatCard 
          title="Custo por Clique (CPC)" 
          value={`R$ ${stats.averageCPC.value}`} 
          icon={MdOutlineAttachMoney} 
          change={stats.averageCPC.change} 
          colorScheme="red" 
          isLoading={isLoading}
          footerText="Valor médio"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Promoções Ativas" 
          value={stats.activePromos.value} 
          icon={MdOutlineCampaign} 
          change={stats.activePromos.change} 
          colorScheme="cyan" 
          isLoading={isLoading}
          footerText="Campanhas em andamento"
        />
        <StatCard 
          title="Taxa de Engajamento" 
          value={`${stats.engagementRate.value}%`} 
          icon={FiShare2} 
          change={stats.engagementRate.change} 
          colorScheme="yellow" 
          isLoading={isLoading}
          footerText="Interações com conteúdo"
        />
        <StatCard 
          title="Downloads do App" 
          value={stats.appDownloads.value} 
          icon={FiDownload} 
          change={stats.appDownloads.change} 
          colorScheme="teal"
          formatter={formatNumber} 
          isLoading={isLoading}
          footerText="Novas instalações"
        />
        <StatCard 
          title="ROI Marketing" 
          value={`${stats.roi.value}%`} 
          icon={FiBarChart2} 
          change={stats.roi.change} 
          colorScheme="orange" 
          isLoading={isLoading}
          footerText="Retorno sobre investimento"
        />
      </Grid>

      {/* Active Campaigns */}
      <Card bg={cardBg} boxShadow="md" mb={6}>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Campanhas Ativas</Heading>
            <Badge colorScheme="green" fontSize="sm" borderRadius="full" px={3} py={1}>
              {isLoading ? '-' : campaigns.filter(c => c.status === 'Ativa').length} campanhas
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>Campanha</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Orçamento</Th>
                    <Th isNumeric>Gasto</Th>
                    <Th isNumeric>Conversões</Th>
                    <Th isNumeric>CPA</Th>
                    <Th isNumeric>ROI (%)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {campaigns.map(campaign => (
                    <Tr key={campaign.id}>
                      <Td fontWeight="medium">{campaign.name}</Td>
                      <Td>
                        <Badge colorScheme={campaign.status === 'Ativa' ? 'green' : 'gray'}>
                          {campaign.status}
                        </Badge>
                      </Td>
                      <Td isNumeric>{formatCurrency(campaign.budget)}</Td>
                      <Td isNumeric>{formatCurrency(campaign.spent)}</Td>
                      <Td isNumeric>{campaign.conversions}</Td>
                      <Td isNumeric>{formatCurrency(campaign.cpa)}</Td>
                      <Td isNumeric>
                        <Text color={campaign.roi > 300 ? 'green.500' : campaign.roi > 200 ? 'teal.500' : 'orange.500'} fontWeight="bold">
                          {campaign.roi}%
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Flex justify="center" mt={4}>
            <Button size="sm" colorScheme="yellow" variant="outline">
              Ver todas as campanhas
            </Button>
          </Flex>
        </CardBody>
      </Card>

      {/* Acquisition Channels & Device Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={0}>
            <Heading size="md">Canais de Aquisição</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>Canal</Th>
                    <Th isNumeric>Usuários</Th>
                    <Th isNumeric>Conv. (%)</Th>
                    <Th isNumeric>ROI (%)</Th>
                    <Th>Desempenho</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {channelStats.map((channel, index) => (
                    <Tr key={index}>
                      <Td fontWeight="medium">{channel.channel}</Td>
                      <Td isNumeric>{formatNumber(channel.users)}</Td>
                      <Td isNumeric>{channel.conversion}%</Td>
                      <Td isNumeric>
                        {channel.roi ? (
                          <Text color={channel.roi > 300 ? 'green.500' : channel.roi > 200 ? 'teal.500' : 'orange.500'} fontWeight="bold">
                            {channel.roi}%
                          </Text>
                        ) : (
                          <Text color="gray.500">N/A</Text>
                        )}
                      </Td>
                      <Td>
                        <Progress 
                          value={channel.users / 50} 
                          size="xs" 
                          colorScheme={
                            channel.conversion > 4 ? 'green' : 
                            channel.conversion > 3 ? 'blue' : 
                            channel.conversion > 2 ? 'yellow' : 'orange'
                          } 
                          borderRadius="full"
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={0}>
            <Heading size="md">Dispositivos</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Grid templateColumns="repeat(3, 1fr)" gap={4} textAlign="center">
                  {deviceStats.map((device, index) => (
                    <Box key={index}>
                      <CircularProgress 
                        value={device.percentage} 
                        color={
                          device.type === 'Desktop' ? 'blue.400' :
                          device.type === 'Mobile' ? 'green.400' : 'purple.400'
                        }
                        size="100px"
                      >
                        <CircularProgressLabel>
                          {device.percentage}%
                        </CircularProgressLabel>
                      </CircularProgress>
                      <Box mt={2}>
                        <HStack justify="center" mb={1}>
                          <Icon as={
                            device.type === 'Desktop' ? FiMonitor :
                            device.type === 'Mobile' ? FiSmartphone : FiTablet
                          } />
                          <Text fontWeight="bold">{device.type}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                          Conversão: {device.conversion}%
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </Grid>

                <Divider />

                <Box>
                  <Text fontWeight="medium" mb={2}>Objetivos de Aquisição</Text>
                  <Stack spacing={2}>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Mobile</Text>
                      <Badge colorScheme="green">+5% vs mês anterior</Badge>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Desktop</Text>
                      <Badge colorScheme="red">-2% vs mês anterior</Badge>
                    </Flex>
                  </Stack>
                </Box>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Content Performance */}
      <Card bg={cardBg} boxShadow="md">
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Desempenho de Conteúdo</Heading>
          </Flex>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6}>
              <Box>
                <Flex align="center" mb={3}>
                  <Icon as={MdOutlineBrush} color="blue.500" mr={2} boxSize={5} />
                  <Text fontWeight="bold">Publicações em Blog</Text>
                </Flex>
                <Stack spacing={3}>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Visualizações</Text>
                    <Text fontSize="sm" fontWeight="medium">12,450</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Tempo Médio</Text>
                    <Text fontSize="sm" fontWeight="medium">3m 42s</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Taxa de Conversão</Text>
                    <Text fontSize="sm" fontWeight="medium">2.8%</Text>
                  </Flex>
                </Stack>
              </Box>

              <Box>
                <Flex align="center" mb={3}>
                  <Icon as={MdOutlinePhotoCamera} color="green.500" mr={2} boxSize={5} />
                  <Text fontWeight="bold">Redes Sociais</Text>
                </Flex>
                <Stack spacing={3}>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Interações</Text>
                    <Text fontSize="sm" fontWeight="medium">45,720</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Compartilhamentos</Text>
                    <Text fontSize="sm" fontWeight="medium">865</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Cliques</Text>
                    <Text fontSize="sm" fontWeight="medium">5,243</Text>
                  </Flex>
                </Stack>
              </Box>

              <Box>
                <Flex align="center" mb={3}>
                  <Icon as={FiMessageCircle} color="purple.500" mr={2} boxSize={5} />
                  <Text fontWeight="bold">Email Marketing</Text>
                </Flex>
                <Stack spacing={3}>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Taxa de Abertura</Text>
                    <Text fontSize="sm" fontWeight="medium">28.3%</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">CTR</Text>
                    <Text fontSize="sm" fontWeight="medium">4.7%</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Conversões</Text>
                    <Text fontSize="sm" fontWeight="medium">372</Text>
                  </Flex>
                </Stack>
              </Box>

              <Box>
                <Flex align="center" mb={3}>
                  <Icon as={MdOutlineWeb} color="yellow.500" mr={2} boxSize={5} />
                  <Text fontWeight="bold">Website</Text>
                </Flex>
                <Stack spacing={3}>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Visitantes Únicos</Text>
                    <Text fontSize="sm" fontWeight="medium">31,852</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Páginas/Visita</Text>
                    <Text fontSize="sm" fontWeight="medium">3.4</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontSize="sm">Taxa de Rejeição</Text>
                    <Text fontSize="sm" fontWeight="medium">42.1%</Text>
                  </Flex>
                </Stack>
              </Box>
            </Grid>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default MarketingDashboard;